import { Fragment, h } from '@stencil/core';
import { Configuration } from '@nylas/core';
import { CONFIGURATION_EVENT_TYPE } from '@/common/constants';

type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[] ? RecursivePartial<U>[] : T[P] extends object | undefined ? RecursivePartial<T[P]> : T[P];
};

/**
 * The props for the Express Flow component.
 */
export type ExpressFlowProps = {
  currentUser: any;
  calendars: any[];
  selectedConfiguration: RecursivePartial<Configuration>;
  selectedEventType: CONFIGURATION_EVENT_TYPE;
};

/**
 * The Express Flow component.
 */
export default function ExpressFlow({ currentUser, calendars, selectedEventType }: ExpressFlowProps) {
  const defaultCalendarOption = calendars?.find(c => c.is_primary) || calendars?.[0];
  switch (selectedEventType) {
    case CONFIGURATION_EVENT_TYPE.one_on_one:
    case CONFIGURATION_EVENT_TYPE.max_availability:
    case CONFIGURATION_EVENT_TYPE.max_fairness: {
      return (
        <Fragment>
          <nylas-event-info>
            <div slot="inputs">
              <nylas-event-title />
              <nylas-event-duration />
              <nylas-event-location userProvider={currentUser.provider} />
            </div>
          </nylas-event-info>
          <nylas-booking-calendar-picker
            exportparts="nbcp, nbcp__header, nbcp__dropdown, nbcp__dropdown-button, nbcp__dropdown-content"
            expressFlow={true}
            calendars={calendars}
            currentUser={currentUser}
            defaultBookingCalendar={defaultCalendarOption?.id}
          />
          <nylas-custom-event-slug name="custom-event-slug" exportparts="nces, nces__body, nces__header, nces__drawer-toggle--container, nces__input-textfield" />
        </Fragment>
      );
    }
    case CONFIGURATION_EVENT_TYPE.collective: {
      return (
        <Fragment>
          <nylas-event-info>
            <div slot="inputs">
              <nylas-event-title />
              <nylas-event-duration />
              <nylas-event-location userProvider={currentUser.provider} />
            </div>
          </nylas-event-info>
          <nylas-booking-calendar-picker
            exportparts="nbcp, nbcp__header, nbcp__dropdown, nbcp__dropdown-button, nbcp__dropdown-content"
            expressFlow={true}
            calendars={calendars}
            currentUser={currentUser}
            defaultBookingCalendar={defaultCalendarOption?.id}
          />
          <nylas-custom-event-slug name="custom-event-slug" exportparts="nces, nces__body, nces__header, nces__drawer-toggle--container, nces__input-textfield" />
          <nylas-additional-participants
            name="participants"
            eventParticipants={[{ name: currentUser?.name || currentUser?.email, email: currentUser?.email || '', is_organizer: true }]}
            exportparts="nadtnlpart, nadtnlpart__title, nadtnlpart__subtitle, nadtnlpart__content, nadtnlpart__input_group, nadtnlpart__input_wrapper, nadtnlpart__input, nadtnlpart__remove-participant"
          />
        </Fragment>
      );
    }
    case CONFIGURATION_EVENT_TYPE.group: {
      return (
        <Fragment>
          <nylas-event-info>
            <div slot="inputs">
              <nylas-event-title noTemplates={true} />
              <nylas-event-description noTemplates={true} hideAutoPopulateButton={true} />
              <nylas-event-location userProvider={currentUser.provider} />
              <nylas-event-timeslot name="event-timeslot" />
              <nylas-event-capacity />
            </div>
          </nylas-event-info>
          <nylas-booking-calendar-picker
            exportparts="nbcp, nbcp__header, nbcp__dropdown, nbcp__dropdown-button, nbcp__dropdown-content"
            expressFlow={true}
            calendars={calendars}
            currentUser={currentUser}
            defaultBookingCalendar={defaultCalendarOption?.id}
          />
          <nylas-additional-participants
            name="participants"
            eventParticipants={[{ name: currentUser?.name || currentUser?.email, email: currentUser?.email || '', is_organizer: true }]}
            exportparts="nadtnlpart, nadtnlpart__title, nadtnlpart__subtitle, nadtnlpart__content, nadtnlpart__input_group, nadtnlpart__input_wrapper, nadtnlpart__input, nadtnlpart__remove-participant"
          />
          <nylas-page-name requirePageName={true} pageName="Group events page" />
          <nylas-custom-event-slug name="custom-event-slug" exportparts="nces, nces__body, nces__header, nces__drawer-toggle--container, nces__input-textfield" />
        </Fragment>
      );
    }
  }
}
